import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { queryKeys } from 'core/api/queryKeys';
import { ApiError } from 'core/errors/ApiError';
import { humanizeApiError, mapFieldErrors } from 'core/errors/mapFieldErrors';
import { paths } from 'core/routing/paths';
import { assessmentApi } from 'features/assessment-dashboard/assessment.api';
import { ResultItemMultiSelect, isCatalogItemAttached } from 'features/top-three-profiles/ResultItemMultiSelect';
import {
  attachedIds,
  buildProfileUpdatePayload,
  canonicalItemType,
  draftFromProfile,
  isDraftDirty,
  itemId,
  missingContentKeys,
} from 'features/top-three-profiles/topThreeProfiles';
import { TagChip, TagChipRow } from 'features/result-content/TagChip';
import {
  ALL_FILTER,
  catalogItemKey,
  fetchResultItemCatalog,
  filterCatalogItems,
  tagFilterOptionsFromTags,
} from 'features/result-content/resultItemCatalog';
import {
  localizedCopy,
  reorderItems,
  requiresSkillAdvice,
  rtlSortableKeyboardCoordinates,
  typeLabelKey,
} from 'features/result-content/resultContent';
import { Button } from 'shared/components/Button';
import { Badge, EmptyState, ErrorState } from 'shared/components/Feedback';
import { ValidationSummary } from 'shared/components/ValidationSummary';
import { useConfirm } from 'shared/components/ConfirmDialog';
import { useToast } from 'shared/components/Toast';
import { SelectField, TextField } from 'shared/forms/Fields';
import { useUnsavedChanges } from 'shared/utilities/useUnsavedChanges';

const RELATION_SECTIONS = [
  { key: 'keyStrengths', type: 'key_strength', titleKey: 'resultContent.tabs.keyStrengths', missingKey: 'profiles.missing.strength' },
  { key: 'growth', type: 'professional_growth_challenge_opportunity', titleKey: 'resultContent.tabs.growth', missingKey: 'profiles.missing.growth' },
  { key: 'careerPaths', type: 'suggested_career_path', titleKey: 'resultContent.tabs.careerPaths', missingKey: 'profiles.missing.careerPath' },
  { key: 'skills', type: 'skill_worth_developing', titleKey: 'resultContent.tabs.skills', missingKey: 'profiles.missing.skill' },
];

function SortableConnectionItem({ id, disabled, label, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  return (
    <article
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.65 : 1 }}
      className="card sortable-item connection-item"
      data-testid="result-connection-item"
    >
      <button type="button" className="drag-handle" aria-label={label} disabled={disabled} {...attributes} {...listeners}>
        ⋮⋮
      </button>
      <div className="connection-item-body">{children}</div>
    </article>
  );
}

function ItemPreview({ item, locale, t }) {
  const tags = item.tags?.length ? item.tags : (item.tag ? [item.tag] : []);
  return (
    <>
      <div className="toolbar wrap">
        <Badge>{t(typeLabelKey(item.canonical_type || item.type))}</Badge>
        {tags.length ? <TagChipRow tags={tags} locale={locale} t={t} /> : <TagChip tag={item.tag} locale={locale} t={t} />}
      </div>
      <h3>{localizedCopy(item, locale)}</h3>
      <p>{localizedCopy(item, locale, 'description')}</p>
      {requiresSkillAdvice(item.type) && (item.advice || item.advice_ar || item.advice_en) ? (
        <p><strong>{t('resultItems.skillAdvice')}:</strong> {localizedCopy(item, locale, 'advice')}</p>
      ) : null}
    </>
  );
}

function sortableId(item) {
  return String(itemId(item) || item.id);
}

export function ProfileConnectionsEditor({
  profileId,
  profile,
  versionId,
  canManage,
  readOnly,
  t,
  locale,
  dir,
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const original = useMemo(() => draftFromProfile(profile), [profile]);
  const [draft, setDraft] = useState(original);
  const [summarySearch, setSummarySearch] = useState('');
  const [selectedSummaryId, setSelectedSummaryId] = useState('');
  const [tagFilter, setTagFilter] = useState(ALL_FILTER);
  const [itemSearch, setItemSearch] = useState('');
  const [selectedByType, setSelectedByType] = useState({});
  const [attachErrors, setAttachErrors] = useState({});
  const [conflictMessage, setConflictMessage] = useState(null);
  const [liveMessage, setLiveMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    setDraft(original);
    setSelectedSummaryId('');
    setSelectedByType({});
    setAttachErrors({});
    setConflictMessage(null);
  }, [original]);

  const dirty = isDraftDirty(draft, original);
  useUnsavedChanges(dirty && canManage && !readOnly);

  const tagsQuery = useQuery({
    queryKey: queryKeys.tags,
    queryFn: ({ signal }) => assessmentApi.listTags({ signal }),
  });
  const summariesQuery = useQuery({
    queryKey: queryKeys.personalSummaries({ search: summarySearch, is_active: 'true', per_page: 50, page: 1 }),
    queryFn: ({ signal }) => assessmentApi.listPersonalSummaries({ search: summarySearch, is_active: true, per_page: 50, page: 1 }, { signal }),
  });
  const catalogQuery = useQuery({
    queryKey: queryKeys.resultItemCatalog,
    queryFn: ({ signal }) => fetchResultItemCatalog(
      (filters, config) => assessmentApi.listResultItems(filters, config),
      { is_active: true },
      { signal },
    ),
  });

  const tags = useMemo(() => tagsQuery.data?.items || [], [tagsQuery.data]);
  const tagOptions = useMemo(() => tagFilterOptionsFromTags(tags, locale, t), [locale, t, tags]);
  const catalogItems = useMemo(() => catalogQuery.data?.items || [], [catalogQuery.data]);
  const availableSummaries = (summariesQuery.data?.items || []).filter((summary) => Number(summary.id) !== Number(draft.personalSummary?.id));

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: rtlSortableKeyboardCoordinates(sortableKeyboardCoordinates, dir) }),
  );

  const missing = missingContentKeys({
    personal_summary: draft.personalSummary,
    key_strengths: draft.keyStrengths,
    growth_opportunities: draft.growth,
    suggested_career_paths: draft.careerPaths,
    skills_worth_developing: draft.skills,
  });

  const persist = useMutation({
    mutationFn: () => assessmentApi.updateTopThreeProfile(versionId, profileId, buildProfileUpdatePayload({
      personalSummaryId: draft.personalSummary?.id ?? null,
      keyStrengths: draft.keyStrengths,
      growth: draft.growth,
      careerPaths: draft.careerPaths,
      skills: draft.skills,
      contentRevision: profile.content_revision ?? profile.revision,
    })),
    onSuccess: async () => {
      setConflictMessage(null);
      setAttachErrors({});
      setSelectedByType({});
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.topThreeProfile(profileId, versionId) }),
        queryClient.invalidateQueries({ queryKey: ['assessment', 'top-three-profiles'] }),
        versionId ? queryClient.invalidateQueries({ queryKey: queryKeys.topThreeProfileCoverage(versionId) }) : Promise.resolve(),
        versionId ? queryClient.invalidateQueries({ queryKey: queryKeys.version(versionId) }) : Promise.resolve(),
        queryClient.invalidateQueries({ queryKey: queryKeys.versions }),
        queryClient.invalidateQueries({ queryKey: queryKeys.overview }),
      ]);
      toast.success(t('status.saved'));
      setStatusMessage(t('status.saved'));
    },
    onError: (error) => {
      if (error instanceof ApiError && error.isForbidden()) {
        navigate(paths.forbidden);
        return;
      }
      if (error instanceof ApiError && error.isValidation()) {
        setAttachErrors(mapFieldErrors(error.errors));
      }
      if (error instanceof ApiError && error.isStaleRevision()) {
        setConflictMessage(humanizeApiError(error, t));
        return;
      }
      if (error instanceof ApiError && error.isConflict()) {
        setConflictMessage(error.message || t('conflict.title'));
        return;
      }
      toast.error(humanizeApiError(error, t));
    },
  });

  const locked = persist.isPending || readOnly || !canManage;

  const updateRelation = (key, items) => {
    setDraft((current) => ({ ...current, [key]: items }));
  };

  const onDragEnd = (key) => ({ active, over }) => {
    if (locked || !active?.id || !over?.id || active.id === over.id) {
      return;
    }
    const current = draft[key];
    const next = reorderItems(current, active.id, over.id);
    if (next === current) {
      const fallback = [...current];
      const oldIndex = fallback.findIndex((item) => sortableId(item) === String(active.id));
      const newIndex = fallback.findIndex((item) => sortableId(item) === String(over.id));
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
        return;
      }
      const [moved] = fallback.splice(oldIndex, 1);
      fallback.splice(newIndex, 0, moved);
      updateRelation(key, fallback);
      setLiveMessage(t('resultContent.dnd.moved', { title: localizedCopy(moved, locale) }));
      return;
    }
    updateRelation(key, next);
    setLiveMessage(t('resultContent.dnd.moved', { title: localizedCopy(next.find((item) => sortableId(item) === String(active.id)), locale) }));
  };

  const moveItem = (key, index, delta) => {
    const current = [...draft[key]];
    const nextIndex = index + delta;
    if (nextIndex < 0 || nextIndex >= current.length) {
      return;
    }
    const [moved] = current.splice(index, 1);
    current.splice(nextIndex, 0, moved);
    updateRelation(key, current);
    setLiveMessage(t('resultContent.dnd.moved', { title: localizedCopy(moved, locale) }));
  };

  const attachSelected = (section) => {
    const keys = selectedByType[section.type] || [];
    if (!keys.length || locked) {
      return;
    }
    const attached = attachedIds(draft[section.key]);
    const selectedItems = keys
      .map((key) => catalogItems.find((item) => catalogItemKey(item) === key))
      .filter((item) => item && item.is_active !== false && !attached.has(String(itemId(item))));
    if (!selectedItems.length) {
      return;
    }
    updateRelation(section.key, [...draft[section.key], ...selectedItems]);
    setSelectedByType((current) => ({ ...current, [section.type]: [] }));
  };

  const revision = profile.content_revision ?? profile.revision;

  return (
    <section className="stack-form" aria-label={t('connections.title')}>
      <div aria-live="polite" className="sr-only">{liveMessage} {statusMessage}</div>
      <h2>{t('connections.title')}</h2>
      {readOnly ? (
        <p className="warning-banner" role="status">{t('profiles.publishedReadOnly')}</p>
      ) : null}
      {conflictMessage ? (
        <p className="warning-banner" role="alert">
          {conflictMessage} {t('conflict.keepLocal')}
        </p>
      ) : null}
      {revision != null ? (
        <p className="muted" data-testid="expected-revision">
          {t('profiles.contentRevision')}: {revision}
        </p>
      ) : null}
      {missing.length > 0 ? (
        <div className="warning-banner" role="status" data-testid="missing-required-content">
          <p>{t('profiles.missingRequired')}</p>
          <ul>
            {missing.map((key) => (
              <li key={key}>{t(key === 'personal_summary' ? 'profiles.missing.personalSummary' : (
                RELATION_SECTIONS.find((section) => section.type === key)?.missingKey || 'profiles.incomplete'
              ))}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <ValidationSummary errors={attachErrors} title={t('forms.validationSummary')} />

      <div className="card">
        <h3>{t('resultContent.tabs.summaries')}</h3>
        <p className="muted">{t('connections.oneSummary')}</p>
        {draft.personalSummary ? (
          <div className="summary-preview">
            <h4>{localizedCopy(draft.personalSummary, locale)}</h4>
            <p>{localizedCopy(draft.personalSummary, locale, 'description')}</p>
            <TagChipRow tags={draft.personalSummary.tags || []} locale={locale} t={t} />
            {canManage && !readOnly ? (
              <Button
                tone="danger"
                disabled={locked}
                onClick={async () => {
                  if (await confirm({ body: t('confirm.detachSummary') })) {
                    setDraft((current) => ({ ...current, personalSummary: null }));
                  }
                }}
              >
                {t('actions.detach')}
              </Button>
            ) : null}
          </div>
        ) : (
          <EmptyState title={t('connections.noSummary')} />
        )}
        {canManage && !readOnly ? (
          <div className="stack-form">
            <TextField
              id="summary-search"
              label={t('connections.searchSummaries')}
              value={summarySearch}
              onChange={setSummarySearch}
            />
            <SelectField
              id="attach-summary"
              label={t('connections.attachSummary')}
              value={selectedSummaryId}
              onChange={setSelectedSummaryId}
              options={[{ value: '', label: t('filters.all') }, ...availableSummaries.map((item) => ({
                value: String(item.id),
                label: localizedCopy(item, locale),
              }))]}
            />
            <Button
              disabled={!selectedSummaryId || locked}
              onClick={() => {
                const next = (summariesQuery.data?.items || []).find((item) => String(item.id) === String(selectedSummaryId));
                if (!next) {
                  return;
                }
                setDraft((current) => ({ ...current, personalSummary: next }));
                setSelectedSummaryId('');
              }}
            >
              {draft.personalSummary ? t('connections.changeSummary') : t('actions.attach')}
            </Button>
            <Link className="btn btn-secondary" to={`${paths.resultContent}?tab=summaries`}>
              {t('connections.createSummaryShortcut')}
            </Link>
          </div>
        ) : null}
      </div>

      {RELATION_SECTIONS.map((section) => {
        const items = draft[section.key] || [];
        const attached = attachedIds(items);
        const selectedKeys = selectedByType[section.type] || [];
        const visible = filterCatalogItems(catalogItems, {
          typeFilter: section.type,
          tagFilter,
          search: itemSearch,
          tagFilterOptions: tags,
        });
        const unattachedVisible = visible.filter((item) => !isCatalogItemAttached(item, attached) && canonicalItemType(item.canonical_type || item.type) === section.type);
        const catalogState = catalogQuery.isLoading
          ? 'loading'
          : catalogQuery.isError
            ? 'error'
            : catalogItems.length === 0
              ? 'empty'
              : visible.length === 0
                ? 'no-match'
                : unattachedVisible.length === 0
                  ? 'all-attached'
                  : 'ready';
        return (
          <div key={section.type} className="card" data-testid={`profile-relation-${section.type}`}>
            <h3>{t(section.titleKey)}</h3>
            <p className="muted">{t('profiles.relationOrderHint')}</p>
            {items.length === 0 ? <EmptyState title={t('connections.noItems')} /> : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onDragEnd(section.key)}
                accessibility={{
                  announcements: {
                    onDragStart({ active }) {
                      const item = items.find((row) => sortableId(row) === String(active.id));
                      return t('resultContent.dnd.lifted', { title: localizedCopy(item, locale) });
                    },
                    onDragOver({ active, over }) {
                      const item = items.find((row) => sortableId(row) === String(active.id));
                      const overItem = items.find((row) => sortableId(row) === String(over?.id));
                      return t('resultContent.dnd.over', {
                        title: localizedCopy(item, locale),
                        target: localizedCopy(overItem, locale),
                      });
                    },
                    onDragEnd({ active }) {
                      const item = items.find((row) => sortableId(row) === String(active.id));
                      return t('resultContent.dnd.dropped', { title: localizedCopy(item, locale) });
                    },
                    onDragCancel({ active }) {
                      const item = items.find((row) => sortableId(row) === String(active.id));
                      return t('resultContent.dnd.cancelled', { title: localizedCopy(item, locale) });
                    },
                  },
                }}
              >
                <SortableContext items={items.map(sortableId)} strategy={verticalListSortingStrategy}>
                  {items.map((item, index) => (
                    <SortableConnectionItem
                      key={sortableId(item)}
                      id={sortableId(item)}
                      disabled={locked}
                      label={t('builder.dragHandle')}
                    >
                      <ItemPreview item={item} locale={locale} t={t} />
                      {canManage && !readOnly ? (
                        <div className="toolbar wrap">
                          <Button tone="ghost" disabled={locked || index === 0} onClick={() => moveItem(section.key, index, -1)}>
                            {t('actions.moveUp')}
                          </Button>
                          <Button tone="ghost" disabled={locked || index === items.length - 1} onClick={() => moveItem(section.key, index, 1)}>
                            {t('actions.moveDown')}
                          </Button>
                          <Button
                            tone="danger"
                            disabled={locked}
                            onClick={async () => {
                              if (await confirm({ body: t('confirm.detachItem') })) {
                                updateRelation(section.key, items.filter((row) => itemId(row) !== itemId(item)));
                              }
                            }}
                          >
                            {t('actions.detach')}
                          </Button>
                        </div>
                      ) : null}
                    </SortableConnectionItem>
                  ))}
                </SortableContext>
              </DndContext>
            )}
            <div className="stack-form" data-testid={section.type === 'key_strength' ? 'result-item-attach' : `result-item-attach-${section.type}`}>
              <SelectField
                id={`attach-item-tag-${section.type}`}
                label={t('resultContent.filterByTag')}
                value={tagFilter}
                onChange={setTagFilter}
                options={tagOptions}
              />
              <TextField
                id={`attach-item-search-${section.type}`}
                label={t('actions.search')}
                value={itemSearch}
                onChange={setItemSearch}
              />
              {catalogState === 'loading' ? <p role="status">{t('connections.loadingCatalog')}</p> : null}
              {catalogState === 'error' ? (
                <ErrorState
                  title={humanizeApiError(catalogQuery.error, t) || t('connections.catalogError')}
                  onRetry={catalogQuery.refetch}
                  retryLabel={t('actions.retry')}
                />
              ) : null}
              {catalogState === 'empty' ? (
                <EmptyState
                  title={t('connections.catalogEmpty')}
                  action={(
                    <Link className="btn btn-secondary" to={paths.resultContent}>
                      {t('connections.openResultContent')}
                    </Link>
                  )}
                />
              ) : null}
              {catalogState === 'no-match' ? (
                <EmptyState
                  title={t('connections.noFilterMatches')}
                  action={<Button tone="ghost" onClick={() => { setTagFilter(ALL_FILTER); setItemSearch(''); }}>{t('connections.clearFilters')}</Button>}
                />
              ) : null}
              {catalogState === 'all-attached' ? <p role="status">{t('connections.allAttached')}</p> : null}
              {catalogState === 'ready' || catalogState === 'all-attached' ? (
                <ResultItemMultiSelect
                  id={`attach-${section.type}`}
                  catalogItems={catalogItems}
                  visibleItems={visible}
                  selectedKeys={selectedKeys}
                  attachedKeys={[...attached]}
                  onChange={(keys) => setSelectedByType((current) => ({ ...current, [section.type]: keys }))}
                  onSelectAllVisible={() => {
                    const next = [...selectedKeys];
                    const seen = new Set(next);
                    unattachedVisible.forEach((item) => {
                      const key = catalogItemKey(item);
                      if (key && !seen.has(key)) {
                        seen.add(key);
                        next.push(key);
                      }
                    });
                    setSelectedByType((current) => ({ ...current, [section.type]: next }));
                  }}
                  onClear={() => setSelectedByType((current) => ({ ...current, [section.type]: [] }))}
                  onRemove={(key) => setSelectedByType((current) => ({
                    ...current,
                    [section.type]: (current[section.type] || []).filter((value) => value !== key),
                  }))}
                  locale={locale}
                  t={t}
                  disabled={locked}
                  canManage={canManage && !readOnly}
                  mixedTypesHint={false}
                />
              ) : null}
              {canManage && !readOnly ? (
                <Button
                  disabled={!selectedKeys.length || locked}
                  onClick={() => attachSelected(section)}
                >
                  {t('connections.attachSelected', { count: selectedKeys.length })}
                </Button>
              ) : null}
            </div>
          </div>
        );
      })}

      {canManage && !readOnly ? (
        <div className="toolbar wrap">
          <Button
            onClick={() => persist.mutate()}
            loading={persist.isPending}
            disabled={!dirty || persist.isPending}
            data-testid="save-profile-connections"
          >
            {t('actions.save')}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
